'use client';

import { useEffect, useRef, useState } from 'react';

import classnames from 'classnames';

import { ButtonGroup } from '../../../../forms/button-group/ButtonGroup';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../tooltip';
import { alphabet, preview } from '../../../utils';
import type { AssessmentAnswersFormProps } from './AssessmentAnswersForm.types';

import styles from './AssessmentAnswersForm.module.scss';

/**
 * Kept to the row rhythm so columns pack as tightly as they can. `space-evenly` shares
 * out whatever is left over, so this is a floor rather than the gap that gets rendered.
 */
const COLUMN_GAP = 10;

/** Only used before a row has been laid out and can be measured. */
const FALLBACK_ROW_WIDTH = 240;

function AssessmentAnswersForm({
  style,
  className,
  questions,
  disabled = false,
  questionsRequiringReview,
  onSelect,
  testId = 'ui-assessment-answers-form',
}: AssessmentAnswersFormProps) {
  const rowsRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);

  // Filling column-major needs an explicit row count, which in turn needs to know how
  // many columns fit — `repeat(auto-fill, …)` cannot work it out in the block axis
  // without a definite height. The width of a real row is measured rather than assumed,
  // because a question can carry three, four or five choices and each of those is a
  // different width; guessing high would leave columns on the table.
  useEffect(() => {
    const node = rowsRef.current;

    if (!node) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const available = entry?.contentRect.width ?? 0;
      const firstRow = node.firstElementChild;

      // Fractional, so a row that is a fraction wider than an exact fit does not get
      // rounded down into a column that then overflows.
      const measured =
        firstRow instanceof HTMLElement
          ? firstRow.getBoundingClientRect().width
          : 0;

      const rowWidth = measured > 0 ? measured : FALLBACK_ROW_WIDTH;

      setColumns(
        Math.max(
          1,
          Math.floor((available + COLUMN_GAP) / (rowWidth + COLUMN_GAP)),
        ),
      );
    });

    observer.observe(node);

    return () => observer.disconnect();
    // Not the array itself: answering a question replaces it, and re-measuring on every
    // click would tear the observer down and build it again for an identical result.
  }, [questions.length]);

  const rowsPerColumn = Math.max(1, Math.ceil(questions.length / columns));

  const requiresReview = new Set(questionsRequiringReview);

  return (
    <div
      style={style}
      className={classnames(styles['main'], className)}
      data-testid={testId}
    >
      <div
        ref={rowsRef}
        className={styles['rows']}
        style={{
          gridTemplateRows: `repeat(${rowsPerColumn}, auto)`,
          columnGap: `${COLUMN_GAP}px`,
        }}
      >
        {questions.map((question, index) => (
          <div
            key={question.id}
            className={styles['row']}
            data-testid={`${testId}_row_${index}`}
          >
            {question.text ? (
              <Tooltip placement="top" mouseOnly={true}>
                <TooltipTrigger asChild={true}>
                  <span className={styles['number']}>{index + 1}</span>
                </TooltipTrigger>
                <TooltipContent className={styles['tooltip']}>
                  {preview(question.text)}
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className={styles['number']}>{index + 1}</span>
            )}
            <ButtonGroup
              // One button per choice the question actually has, lettered the same way
              // the question screen letters them — assessments are built with three,
              // four or five choices, so a fixed A–D would not line up.
              options={question.options.map((_, optionIndex) => ({
                value: optionIndex + 1,
                label: alphabet(optionIndex),
                selected: question.selectedOptionIndex === optionIndex + 1,
              }))}
              size="sm"
              disabled={disabled}
              warning={requiresReview.has(question.id)}
              label={`Pregunta ${index + 1}`}
              testId={`${testId}_options_${index}`}
              onSelect={(value) => onSelect(question.id, Number(value))}
              onDeselect={() => onSelect(question.id, 0)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export { AssessmentAnswersForm };
